import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { deleteAuthoringDraft } from '../../api/authoringApi';
import type { AuthoringDraft } from '../../types/authoring.types';

const draftsKey = ['eqms', 'authoring-drafts'] as const;

export function DeleteAuthoringDraftDialog({ draft, onClose, onRestoreFocus }: {
  draft: AuthoringDraft; onClose: () => void; onRestoreFocus: () => void;
}) {
  const { t } = useTranslation('qms');
  const queryClient = useQueryClient();
  const deletion = useMutation({
    mutationFn: () => deleteAuthoringDraft(draft.id, draft.lockVersion),
    retry: false,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['eqms', 'authoring', draft.id], exact: true });
      queryClient.setQueryData<{ drafts: AuthoringDraft[] }>(draftsKey, current => current
        ? { ...current, drafts: current.drafts.filter(item => item.id !== draft.id) } : current);
      void queryClient.invalidateQueries({ queryKey: draftsKey });
      toast.success(t('authoring.deleted'));
      onClose();
    },
    onError: error => {
      if (error instanceof ApiError && error.status === 409) void queryClient.invalidateQueries({ queryKey: draftsKey });
    },
  });
  const conflict = deletion.error instanceof ApiError && deletion.error.status === 409;

  return (
    <AlertDialog open onOpenChange={open => { if (!open && !deletion.isPending) onClose(); }}>
      <AlertDialogContent onCloseAutoFocus={event => { event.preventDefault(); onRestoreFocus(); }}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('authoring.deleteTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('authoring.deleteDescription', { name: draft.title || t('authoring.untitled') })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {deletion.isError && <p role="alert" className="text-sm text-red-700">
          {t(conflict ? 'authoring.deleteConflict' : 'authoring.deleteFailed')}
        </p>}
        <AlertDialogFooter>
          <AlertDialogCancel asChild><Button variant="outline" disabled={deletion.isPending}>{t('authoring.cancel')}</Button></AlertDialogCancel>
          <Button variant="destructive" disabled={deletion.isPending || conflict} onClick={() => deletion.mutate()}>
            {t(deletion.isPending ? 'authoring.deleting' : 'authoring.deleteConfirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
